import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField
} from '@mui/material';
import moment from 'moment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { zhTW } from 'date-fns/locale';
import CctvDetectionTypeChart from '../components/CctvDetectionTypeChart';
import { getCctvList, getCctvListNewTaipei2, getMonthlyDetectionByType } from '../services/cctvService';

const CctvDetectionDashboard = () => {
    const { teams } = useSelector((state) => state.teamsInfo);
    const { users } = useSelector((state) => state.userLogin);
    // 固定使用 newtaipei2
    const teamName = 'newtaipei2';

    const [cctvData, setCctvData] = useState([]);

    // 月報表下載用狀態
    const [reportCamera, setReportCamera] = useState('');
    const [reportMonth, setReportMonth] = useState(moment().format('YYYY-MM'));

    const fetchCctvData = useCallback(async () => {
        try {
            const data = await getCctvListNewTaipei2();
            setCctvData(data);
            if (data.length > 0) {
                setReportCamera(data[0].code);
            }
        } catch (error) {
            console.error('fetchCctvData 錯誤:', error);
        }
    }, [teamName]);

    useEffect(() => {
        if (teamName) {
            fetchCctvData();
        }
    }, [teamName, fetchCctvData]);

    // 月報表 CSV 下載
    const handleMonthlyReportDownload = async () => {
        if (!reportCamera || !reportMonth) return;
        try {
            const data = await getMonthlyDetectionByType(reportCamera, reportMonth, teamName);
            const { headers, data: csvRows, month } = data;

            // 以 BOM 開頭確保 Excel 正確顯示中文
            const BOM = '\uFEFF';
            let csvContent = `${BOM}${headers.join(',')}\n`;
            csvRows.forEach((row) => {
                csvContent += `${row.join(',')}\n`;
            });

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');

            const cameraName =
                cctvData.find((c) => c.code === reportCamera)?.name || reportCamera;
            link.setAttribute('href', url);
            link.setAttribute('download', `${cameraName}_${month}_各類型偵測月統計.csv`);
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('月報表下載失敗:', error);
        }
    };

    return (
        <Box sx={{ flexGrow: 1, p: 3 }}>
            <Grid container spacing={2}>

                {/* 圖表區塊 */}
                <Grid item xs={12}>
                    <Paper sx={{ height: 600, p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            每日攝影機各類型偵測統計
                        </Typography>
                        <CctvDetectionTypeChart
                            cctvData={cctvData}
                            teamName={teamName}
                        />
                    </Paper>
                </Grid>

                {/* 月報表下載區塊 */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            各類型偵測月報表下載
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>

                            {/* 攝影機選擇 */}
                            <FormControl variant="standard" sx={{ minWidth: 180 }}>
                                <InputLabel>選擇攝影機</InputLabel>
                                <Select
                                    value={reportCamera}
                                    onChange={(e) => setReportCamera(e.target.value)}
                                >
                                    {cctvData.map((cctv) => (
                                        <MenuItem key={cctv.code} value={cctv.code}>
                                            {cctv.name || cctv.code}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* 月份選擇 */}
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={zhTW}>
                                <DatePicker
                                    views={['year', 'month']}
                                    label="選擇月份"
                                    value={reportMonth ? new Date(`${reportMonth}-01T00:00:00`) : new Date()}
                                    onChange={(newVal) => {
                                        if (newVal) setReportMonth(moment(newVal).format('YYYY-MM'));
                                    }}
                                    maxDate={new Date()}
                                    renderInput={(params) => (
                                        <TextField {...params} variant="standard" sx={{ width: 160 }} />
                                    )}
                                />
                            </LocalizationProvider>

                            {/* 下載按鈕 */}
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleMonthlyReportDownload}
                                disabled={!reportCamera || !reportMonth}
                            >
                                下載月報表 (CSV)
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

            </Grid>
        </Box>
    );
};

export default CctvDetectionDashboard;
